import type { GreetingEnergy, RedactionLevel } from "@zoidberg/contract";

/**
 * The editable profile the config PWA owns (the P0 config surface, §11):
 * routine durations, default/empty-morning wake, phone numbers, redaction,
 * persona. Persisted to localStorage for now behind this thin API; swapping to
 * the brain's HTTP config endpoint later is a one-file change (same shape).
 */
export interface ProfileConfig {
  timezone: string;
  redactionLevel: RedactionLevel;
  phones: { primary: string; backups: string[] };
  routine: {
    /** Comfortable morning routine; drives T₀. Prior 90 min. */
    comfortMin: number;
    /** Hard-wake minimum ("at least an hour"). Prior 60 min. */
    minimumMin: number;
  };
  emptyMorning: {
    /** Wake when bedtime unknown and nothing's scheduled. Prior 9:00 → 540. */
    fallbackMin: number;
    /** Latest the day may start so it doesn't evaporate. 11:00 → 660. */
    latestMin: number;
  };
  persona: {
    voiceLabel: string;
    speed: number;
    greetingEnergy: GreetingEnergy;
  };
}

export const DEFAULT_CONFIG: ProfileConfig = {
  timezone: "America/New_York",
  redactionLevel: "initials",
  phones: { primary: "+13055550100", backups: [] },
  routine: { comfortMin: 90, minimumMin: 60 },
  emptyMorning: { fallbackMin: 9 * 60, latestMin: 11 * 60 },
  persona: { voiceLabel: "Warm — Aria", speed: 1.0, greetingEnergy: "gentle" },
};

import { syncConfigToCloud, fetchConfigFromCloud } from "./cloud";

// ... rest of the file stays the same ...

const KEY = "zoidberg.config.v1";

export function loadConfig(): ProfileConfig {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULT_CONFIG;
    return { ...DEFAULT_CONFIG, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_CONFIG;
  }
}

export function saveConfig(c: ProfileConfig): void {
  localStorage.setItem(KEY, JSON.stringify(c));
  // Background sync to cloud — never blocks the UI
  syncConfigToCloud(c).catch(() => {});
}

/** Pull the latest config from the cloud (call on app init). */
export async function hydrateConfig(): Promise<ProfileConfig | null> {
  const cloud = await fetchConfigFromCloud();
  if (cloud) {
    const merged = { ...DEFAULT_CONFIG, ...cloud };
    localStorage.setItem(KEY, JSON.stringify(merged));
    return merged;
  }
  return null;
}
